import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { ExportComplianceNotice } from './ExportComplianceNotice';

const ENQUIRE = '/contact#enquiry-form';

const FOCUS = [
  {
    name: 'GPU & AI Compute',
    description: 'Datacentre GPU accelerators, professional GPUs and multi-GPU AI/HPC systems for compute-intensive deployments.',
    image: '/hero/gpu-accelerator.webp',
    to: '/products?category=gpu-ai-compute',
  },
  {
    name: 'Enterprise Components',
    description: 'Server and desktop processors, platform components and core silicon for system builders.',
    image: '/hero/processor.webp',
    to: '/products?category=cpus',
  },
  {
    name: 'Servers & Workstations',
    description: 'Rack servers and high-performance workstations for infrastructure providers and integrators.',
    image: '/hero/rack-server.webp',
    to: '/products?category=servers',
  },
  {
    name: 'Storage & Memory',
    description: 'NVMe and enterprise SSDs alongside high-capacity server memory, available in volume.',
    image: '/hero/nvme-ssd.webp',
    to: '/products?category=enterprise-storage',
  },
  {
    name: 'Networking',
    description: 'Switches and datacentre connectivity hardware to complete infrastructure builds.',
    image: '/hero/network-switch.webp',
    to: '/products?category=networking',
  },
];

const CONSUMER = {
  name: 'Consumer Technology',
  description: 'Laptops, smartphones, audio, wearables and lifestyle electronics remain available as a secondary category through our live wholesale catalogue.',
  image: '/hero/consumer-tech.webp',
  to: '/products?category=consumer-technology',
};

const slug = (s) => s.toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const cardShell = 'rounded-3xl bg-white border border-slate-200/70 overflow-hidden group shadow-[0_8px_30px_rgba(15,23,42,0.04)] hover:shadow-[0_20px_40px_rgba(15,23,42,0.08)] hover:-translate-y-1.5 transition-[transform,box-shadow] duration-500';

const FocusCard = ({ item }) => (
  <Link to={item.to || ENQUIRE} className={`block h-full ${cardShell}`} data-testid={`focus-card-${slug(item.name)}`}>
    <div className="aspect-[4/3] bg-white border-b border-slate-100 p-6 overflow-hidden">
      <img src={item.image} alt={item.name} loading="lazy" decoding="async" width={1200} height={900} className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105" />
    </div>
    <div className="p-6 md:p-7">
      <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-2">{item.name}</h3>
      <p className="font-manrope text-sm text-slate-600 leading-relaxed mb-4">{item.description}</p>
      <span className="inline-flex items-center gap-1 font-manrope text-sm font-semibold text-[#0096D6]">
        View collection <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
      </span>
    </div>
  </Link>
);

const PrimaryFocusCard = ({ item }) => (
  <Link to={item.to || ENQUIRE} className={`flex flex-col md:flex-row h-full ${cardShell}`} data-testid={`focus-card-${slug(item.name)}`}>
    <div className="p-8 md:p-10 flex flex-col justify-between md:w-1/2">
      <div>
        <span className="inline-block px-3 py-1 rounded-full bg-[#0096D6]/10 text-[#0096D6] font-manrope text-[11px] font-semibold tracking-[0.14em] uppercase mb-4">Primary Focus</span>
        <h3 className="font-outfit text-2xl md:text-3xl font-bold text-slate-900 mb-3">{item.name}</h3>
        <p className="font-manrope text-slate-600 leading-relaxed">{item.description}</p>
      </div>
      <span className="inline-flex items-center gap-1 font-manrope text-sm font-semibold text-[#0096D6] mt-6">
        View collection <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
      </span>
    </div>
    <div className="md:w-1/2 aspect-[4/3] md:aspect-auto bg-white border-t md:border-t-0 md:border-l border-slate-100 p-6 md:p-8 overflow-hidden flex items-center">
      <img src={item.image} alt={item.name} loading="lazy" decoding="async" width={1200} height={900} className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105" />
    </div>
  </Link>
);

export const ProductShowcase = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.12 } }
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
  };

  return (
    <section className="section-padding bg-white relative overflow-hidden" data-testid="products-showcase">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6"
        >
          <div>
            <span className="inline-block px-4 py-2 rounded-full bg-[#0096D6]/10 text-[#0096D6] font-manrope text-sm font-medium mb-4">
              Hardware Focus
            </span>
            <h2 className="font-outfit text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Built Around High-Performance Compute
            </h2>
            <p className="font-manrope text-slate-600 text-lg max-w-xl">
              Our commercial focus runs from GPU and AI infrastructure through enterprise components, servers and storage to networking, with consumer technology available as a secondary category.
            </p>
          </div>
          <Link to="/products" className="btn-secondary inline-flex items-center gap-2 whitespace-nowrap" data-testid="view-all-products-btn">
            View All Products
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          <motion.div variants={itemVariants} className="md:col-span-2">
            <PrimaryFocusCard item={FOCUS[0]} />
          </motion.div>
          {FOCUS.slice(1).map((item) => (
            <motion.div key={item.name} variants={itemVariants}>
              <FocusCard item={item} />
            </motion.div>
          ))}

          <motion.div variants={itemVariants} className="md:col-span-2 lg:col-span-3">
            <Link
              to={CONSUMER.to}
              className="flex flex-col sm:flex-row items-center gap-6 rounded-3xl bg-slate-50 border border-slate-200/70 p-6 md:px-8 group hover:border-slate-300 transition-colors duration-300"
              data-testid={`focus-card-${slug(CONSUMER.name)}`}
            >
              <div className="w-40 h-24 shrink-0 rounded-2xl bg-white border border-slate-200/70 p-2 flex items-center justify-center">
                <img src={CONSUMER.image} alt={CONSUMER.name} loading="lazy" decoding="async" width={1200} height={900} className="w-full h-full object-contain" />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <div className="flex flex-wrap items-center gap-3 justify-center sm:justify-start mb-1">
                  <h3 className="font-outfit text-xl font-semibold text-slate-900">{CONSUMER.name}</h3>
                  <span className="px-2.5 py-0.5 rounded-full border border-slate-200 text-slate-400 font-manrope text-[10px] font-semibold tracking-[0.14em] uppercase">Secondary Category</span>
                </div>
                <p className="font-manrope text-sm text-slate-600 leading-relaxed">{CONSUMER.description}</p>
              </div>
              <span className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-300 bg-white font-manrope text-sm font-semibold text-slate-700 group-hover:border-[#0096D6]/50 group-hover:text-[#0096D6] transition-colors duration-300 whitespace-nowrap">
                Browse Collection <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </motion.div>
        </motion.div>

        <ExportComplianceNotice />
      </div>
    </section>
  );
};
